import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Award, HelpCircle } from 'lucide-react';
import { AthleteProfile, SportType } from '../types';

interface PercentileStatTooltipProps {
  statLabel: string;
  value: number | string;
  unit?: string;
  sport?: SportType | string;
  leagueAvg?: number;
  percentileRank?: number; // 1 to 99
  allAthletes?: AthleteProfile[];
  statKey?: 'pts' | 'reb' | 'ast' | 'winRate';
  children?: React.ReactNode;
  className?: string;
}

export const PercentileStatTooltip: React.FC<PercentileStatTooltipProps> = ({
  statLabel,
  value,
  unit = '',
  sport = 'basketball',
  leagueAvg,
  percentileRank,
  allAthletes,
  statKey,
  children,
  className = '',
}) => {
  const [isHovered, setIsHovered] = useState(false);

  const numericVal = typeof value === 'number' ? value : parseFloat(String(value)) || 0;

  // Calculate true league average & percentile rank from allAthletes if available
  let computedLeagueAvg = leagueAvg;
  let computedPercentile = percentileRank;

  if (allAthletes && allAthletes.length > 0 && statKey) {
    const statValues = allAthletes.map((a) => {
      if (statKey === 'pts') return (a.stats?.basketball?.pts || 0) / (a.stats?.basketball?.gamesPlayed || 1);
      if (statKey === 'reb') return (a.stats?.basketball?.reb || 0) / (a.stats?.basketball?.gamesPlayed || 1);
      if (statKey === 'ast') return (a.stats?.basketball?.ast || 0) / (a.stats?.basketball?.gamesPlayed || 1);
      if (statKey === 'winRate') return (a.winCount + a.lossCount) > 0 ? (a.winCount / (a.winCount + a.lossCount)) * 100 : 0;
      return 0;
    });

    const sum = statValues.reduce((acc, curr) => acc + curr, 0);
    computedLeagueAvg = parseFloat((sum / statValues.length).toFixed(1));

    // Count how many values are lower than current
    const lowerCount = statValues.filter((v) => v < numericVal).length;
    computedPercentile = Math.min(99, Math.max(5, Math.round((lowerCount / statValues.length) * 100)));
  }

  if (computedLeagueAvg === undefined) {
    if (statLabel.toLowerCase().includes('pts') || statLabel.toLowerCase().includes('ppg') || statLabel.toLowerCase().includes('points')) {
      computedLeagueAvg = 14.2;
    } else if (statLabel.toLowerCase().includes('reb') || statLabel.toLowerCase().includes('rpg') || statLabel.toLowerCase().includes('rebounds')) {
      computedLeagueAvg = 5.4;
    } else if (statLabel.toLowerCase().includes('ast') || statLabel.toLowerCase().includes('apg') || statLabel.toLowerCase().includes('assists')) {
      computedLeagueAvg = 4.1;
    } else if (statLabel.toLowerCase().includes('win') || statLabel.toLowerCase().includes('rate')) {
      computedLeagueAvg = 50;
    } else {
      computedLeagueAvg = 10;
    }
  }

  if (computedPercentile === undefined) {
    if (computedLeagueAvg > 0) {
      const ratio = numericVal / computedLeagueAvg;
      computedPercentile = Math.min(99, Math.max(5, Math.round(50 + (ratio - 1) * 35)));
    } else {
      computedPercentile = 75;
    }
  }

  const topPercent = Math.max(1, 100 - computedPercentile);
  const isAboveAvg = numericVal >= computedLeagueAvg;
  const pctDiff = computedLeagueAvg > 0
    ? (((numericVal - computedLeagueAvg) / computedLeagueAvg) * 100).toFixed(1)
    : '0';

  return (
    <div
      className={`relative inline-block ${className}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Target element trigger */}
      {children ? (
        <div className="cursor-help">{children}</div>
      ) : (
        <div className="inline-flex items-center space-x-1 cursor-help hover:text-lime-300 transition">
          <span className="font-extrabold">{value}{unit}</span>
          <HelpCircle className="w-3.5 h-3.5 text-indigo-300/60 hover:text-lime-400" />
        </div>
      )}

      {/* Interactive Floating Glassmorphism Tooltip with framer-motion */}
      <AnimatePresence>
        {isHovered && (
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 5, scale: 0.95 }}
            transition={{ duration: 0.18, ease: 'easeOut' }}
            className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 p-3.5 bg-indigo-950/95 border border-lime-400/50 rounded-2xl shadow-2xl text-white backdrop-blur-xl pointer-events-none"
            style={{ boxShadow: '0 20px 30px -10px rgba(0, 0, 0, 0.8), 0 0 15px rgba(163, 230, 53, 0.2)' }}
          >
            {/* Header */}
            <div className="flex items-center justify-between border-b border-white/10 pb-2 mb-2">
              <span className="text-[10px] font-black uppercase text-lime-400 tracking-wider flex items-center gap-1">
                <Award className="w-3.5 h-3.5 text-amber-400" />
                <span>Percentile Rank</span>
              </span>
              <span className="text-[9px] bg-lime-400/20 text-lime-300 font-bold px-1.5 py-0.5 rounded uppercase">
                {sport}
              </span>
            </div>

            {/* Core Percentile Rank Callout */}
            <div className="space-y-1.5">
              <div className="flex items-baseline justify-between">
                <span className="text-xs font-bold text-indigo-200">{statLabel}</span>
                <span className="text-sm font-black text-lime-400 font-mono">
                  {topPercent <= 10 ? `Top ${topPercent}%` : `${computedPercentile}th Pct`}
                </span>
              </div>

              {/* Visual Percentile Progress Bar */}
              <div className="w-full bg-indigo-900/80 h-2 rounded-full overflow-hidden relative border border-white/10">
                <div
                  className="bg-gradient-to-r from-amber-400 to-lime-400 h-full rounded-full transition-all duration-300"
                  style={{ width: `${computedPercentile}%` }}
                />
              </div>

              {/* League Avg Comparison Details */}
              <div className="bg-indigo-900/60 p-2 rounded-xl border border-white/5 text-[10px] space-y-1 mt-2">
                <div className="flex justify-between text-indigo-200 font-semibold">
                  <span>Athlete Stat:</span>
                  <strong className="text-white font-mono">{numericVal} {unit}</strong>
                </div>
                <div className="flex justify-between text-indigo-200/70">
                  <span>League Average:</span>
                  <span className="text-indigo-300 font-mono">{computedLeagueAvg} {unit}</span>
                </div>
                <div className="flex justify-between pt-1 border-t border-white/10 font-bold">
                  <span>Performance:</span>
                  <span className={isAboveAvg ? 'text-emerald-400' : 'text-rose-400'}>
                    {isAboveAvg ? `+${pctDiff}% vs League Avg` : `${pctDiff}% vs League Avg`}
                  </span>
                </div>
              </div>

              <p className="text-[9px] text-indigo-300/70 italic text-center pt-1">
                Calculated dynamically against active athletes in the league.
              </p>
            </div>

            {/* Pointer Arrow */}
            <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-1 border-4 border-transparent border-t-indigo-950/95" />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

